import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { TicketDetail } from '../../api/types';
import Badge from '../../components/Badge';
import { communicationModeBadgeClass } from './OverviewTab';
import { formatTicketId, isSlaNearBreach, ticketCreatorName } from '../../utils/ticketDisplay';
import { formatLabel, priorityBadgeClass, statusBadgeClass } from '../../utils/ticket';
import { COMMUNICATION_MODE_LABELS, CommunicationMode } from '@21k/shared';

type Tab = 'overview' | 'files' | 'audit';

type Props = {
  ticket: TicketDetail;
  embedded?: boolean;
  onEscalate: () => void;
  onClose: () => void;
  onReopen: () => void;
  closing: boolean;
  reopening: boolean;
  canClose: boolean;
  canReopen: boolean;
  tab: Tab;
  tabItems: Array<readonly [Tab, string]>;
  onTabChange: (tab: Tab) => void;
};

export default function TicketDetailHeader({
  ticket,
  embedded = false,
  onEscalate,
  onClose,
  onReopen,
  closing,
  reopening,
  canClose,
  canReopen,
  tab,
  tabItems,
  onTabChange,
}: Props) {
  const nearBreach = isSlaNearBreach(ticket.slaDeadline);
  const [labelsExpanded, setLabelsExpanded] = useState(false);
  const headerLabelLimit = 2;
  const visibleHeaderLabels = labelsExpanded
    ? ticket.labels
    : ticket.labels.slice(0, headerLabelLimit);
  const hiddenHeaderLabelCount = Math.max(0, ticket.labels.length - headerLabelLimit);

  return (
    <header className="ticket-detail-header">
      <nav className="breadcrumb">
        {embedded ? (
          <>
            <Link to="/tickets" className="ticket-detail-close-list">
              ← Back to list
            </Link>
            <span aria-hidden>·</span>
          </>
        ) : (
          <>
            <Link to="/tickets">Tickets</Link>
            <span aria-hidden>→</span>
          </>
        )}
        <span>{formatTicketId(ticket.id)}</span>
      </nav>
      <div className="ticket-detail-header-main">
        <div className="ticket-detail-header-copy">
          <h1>{ticket.title}</h1>
          <p className="ticket-detail-creator">Created by {ticketCreatorName(ticket)}</p>
          <div className="ticket-detail-badges">
            <Badge label={ticket.status.name} className={statusBadgeClass(ticket.status)} />
            <Badge label={ticket.priority} className={priorityBadgeClass(ticket.priority)} />
            <Badge label={ticket.channelType.name} className="badge-info" />
            <Badge
              label={
                COMMUNICATION_MODE_LABELS[ticket.communicationMode as CommunicationMode] ??
                ticket.communicationMode
              }
              className={communicationModeBadgeClass(
                ticket.communicationMode as CommunicationMode,
              )}
            />
            <Badge label={ticket.category.name} />
            {visibleHeaderLabels.map((label) => (
              <Badge key={label.id} label={formatLabel(label)} className="badge-info" />
            ))}
            {!labelsExpanded && hiddenHeaderLabelCount > 0 && (
              <button
                type="button"
                className="label-badges-toggle"
                onClick={() => setLabelsExpanded(true)}
              >
                +{hiddenHeaderLabelCount} more →
              </button>
            )}
            {labelsExpanded && ticket.labels.length > headerLabelLimit && (
              <button
                type="button"
                className="label-badges-toggle"
                onClick={() => setLabelsExpanded(false)}
              >
                ← show less
              </button>
            )}
            {nearBreach && (
              <Badge label="SLA breach imminent" className="badge-danger" />
            )}
            {ticket.slaBreach && <Badge label="SLA breached" className="badge-danger" />}
          </div>
        </div>
        <div className="ticket-detail-actions">
          {canReopen && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onReopen}
              disabled={reopening}
            >
              {reopening ? 'Reopening…' : 'Reopen'}
            </button>
          )}
          {canClose && (
            <>
              <button type="button" className="btn btn-secondary" onClick={onEscalate}>
                Escalate
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={onClose}
                disabled={closing}
              >
                {closing ? 'Closing…' : 'Close ticket'}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="ticket-detail-tabs">
        {tabItems.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`tab ${tab === key ? 'active' : ''}`}
            onClick={() => onTabChange(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </header>
  );
}

export type { Tab };
